// Pure display and quoting helpers. Internal USD amounts carry 18 decimals; prices are oracle units,
// `priceScale` units per USD (HYPE 10,000).
import { bustPrice, fee, pnl } from '../../../offchain/src/math.ts';

export const WAD = 10n ** 18n;

function group(int: string): string {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Fixed-point bigint to a grouped decimal string, rounded half away from zero. */
export function fmtUnits(v: bigint, decimals: number, dp: number): string {
  const neg = v < 0n;
  let a = neg ? -v : v;
  if (dp < decimals) {
    const unit = 10n ** BigInt(decimals - dp);
    a = (a + unit / 2n) / unit;
  } else {
    a *= 10n ** BigInt(dp - decimals);
  }
  const s = a.toString().padStart(dp + 1, '0');
  const int = group(s.slice(0, s.length - dp));
  const frac = dp > 0 ? `.${s.slice(s.length - dp)}` : '';
  return `${neg && a !== 0n ? '−' : ''}${int}${frac}`;
}

export const fmtUsd = (wad: bigint, dp = 2) => fmtUnits(wad, 18, dp);

export function fmtSignedUsd(wad: bigint, dp = 2): string {
  const s = fmtUsd(wad, dp);
  return wad > 0n ? `+${s}` : s;
}

export function priceDecimals(priceScale: number): number {
  return Math.max(0, Math.round(Math.log10(priceScale)));
}

export const fmtPx = (px: bigint, priceScale: number) => fmtUnits(px, priceDecimals(priceScale), priceDecimals(priceScale));

/** WAD rate to a percent string */
export const fmtRate = (rate: bigint, dp = 2) => `${fmtUnits(rate * 100n, 18, dp)}%`;

/** Exact, ungrouped decimal for an input field (no rounding up past a balance). */
export function toInput(v: bigint, decimals: number): string {
  if (decimals === 0) return v.toString();
  const s = v.toString().padStart(decimals + 1, '0');
  const frac = s.slice(s.length - decimals).replace(/0+$/, '');
  return frac ? `${s.slice(0, s.length - decimals)}.${frac}` : s.slice(0, s.length - decimals);
}

/**
 * Cleans what a user types in an amount field: a comma is a decimal separator (French and most European
 * keyboards), so "1,5" stays 1.5 instead of becoming 15 once parseUnits drops thousands separators.
 */
export function amountInput(typed: string): string {
  return typed.replace(/,/g, '.').replace(/[^\d.]/g, '');
}

/** Parses a user-typed decimal into fixed point; undefined if malformed or over-precise. */
export function parseUnits(input: string, decimals: number): bigint | undefined {
  const s = input.trim().replace(/,/g, '');
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[1] === '' && (m[2] ?? '') === '')) return undefined;
  const frac = m[2] ?? '';
  if (frac.length > decimals) return undefined;
  return BigInt(m[1] || '0') * 10n ** BigInt(decimals) + BigInt(frac.padEnd(decimals, '0') || '0');
}

export const shortAddr = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

/** Signed relative move, in percent, from `from` to `to` */
export function movePct(from: bigint, to: bigint): number {
  return (Number(to - from) / Number(from)) * 100;
}

type Curve = { winHaircut: bigint; curveA: bigint; curveB: bigint; bustBuffer: bigint };

/** What the user receives (or loses) net of fees if the position closes at `exitPx`. */
export function netPnl(
  p: { entryPx: bigint; exitPx: bigint; margin: bigint; leverage: bigint; isLong: boolean },
  curve: Curve,
  winFeeRate: bigint,
): bigint {
  const { credited } = pnl(p, curve);
  if (credited >= 0n) return credited - fee(credited, winFeeRate);
  return credited < -p.margin ? -p.margin : credited;
}

/** Net PnL from a contract preview (`previewClose`) */
export function netFromCredited(credited: bigint, margin: bigint, winFeeRate: bigint): bigint {
  if (credited >= 0n) return credited - fee(credited, winFeeRate);
  return credited < -margin ? -margin : credited;
}

export type Quote = {
  notional: bigint;
  /** Base-asset size, 18 dec, as counted in open interest */
  size: bigint;
  liqPx: bigint;
  liqMovePct: number;
  /** Net PnL at a few price moves, for the payout table */
  scenarios: { pct: number; pnl: bigint }[];
  /** Points of the payout curve between the liquidation and the mirrored move, for the chart */
  curve: { pct: number; pnl: bigint }[];
};

export function quote(
  o: { margin: bigint; leverage: number; isLong: boolean; mid: bigint; priceScale: number },
  curve: Curve,
  winFeeRate: bigint,
): Quote {
  const lev = BigInt(o.leverage);
  const notional = o.margin * lev;
  const size = (notional * BigInt(o.priceScale)) / o.mid;
  const liqPx = bustPrice(o.mid, lev, o.isLong, curve.bustBuffer);
  const liqMovePct = movePct(o.mid, liqPx);
  const at = (pct: number) => {
    const exitPx = o.mid + (o.mid * BigInt(Math.round(pct * 1e6))) / 100_000_000n;
    return netPnl({ entryPx: o.mid, exitPx, margin: o.margin, leverage: lev, isLong: o.isLong }, curve, winFeeRate);
  };
  const reach = Math.abs(liqMovePct);
  const steps = [-1, -0.5, -0.25, 0.25, 0.5, 1, 2].map((k) => k * reach * (o.isLong ? 1 : -1));
  const scenarios = steps.map((pct) => ({ pct, pnl: at(pct) })).sort((a, b) => a.pct - b.pct);
  const N = 48;
  const span = reach * 3;
  const pts = Array.from({ length: N + 1 }, (_, i) => -span + (2 * span * i) / N);
  return { notional, size, liqPx, liqMovePct, scenarios, curve: pts.map((pct) => ({ pct, pnl: at(pct) })) };
}

/** WAD share to a compact percent: 0.05e18 -> "5%" */
export const pct = (wad: bigint) => `${Number((wad * 10_000n) / WAD) / 100}%`;

/**
 * What stakers earn on the house now: nothing while claims are queued, the loss share up to the reward threshold
 * and while the house is under its record (`belowRecord`), and also a share of its gains past the record.
 */
export type RewardPhase = 'queued' | 'losses' | 'belowRecord' | 'newHigh';

export function rewardPhase(effectiveLp: bigint, threshold: bigint, peakExcess: bigint): RewardPhase {
  if (effectiveLp < 0n) return 'queued';
  if (effectiveLp <= threshold) return 'losses';
  return effectiveLp - threshold < peakExcess ? 'belowRecord' : 'newHigh';
}
